import * as d3 from 'd3';
import _ from 'lodash';
import { Granularity } from '../../models';
import React from 'react';
import { NodeGroup } from 'react-move';
import * as S from './chart-bar-group.styles';
import { IChartPoint, IChartTimeline } from './models';
import { createScales, getInterval } from './util';

interface INode {
    key: string;
    state: INodeConfig;
}

interface INodeConfig {
    opacity: number;
    segments: number;
    height: number[];
    width: number[];
    x: number[];
    y: number[];
    timing?: object;
}

export interface IChartBarGroupProps {
    chartRect: ClientRect;
    granularity: Granularity;
    timeline: IChartTimeline;
}

export class ChartBarGroup extends React.Component<IChartBarGroupProps> {
    public render(): React.ReactElement {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        const [lineData, ...barData] = timeline.data;
        return (
            <g>
                {this.renderBars(barData, xScale, yScale)}
                {this.renderLine(lineData, xScale, yScale)}
            </g>
        );
    }

    private readonly renderBars = (
        barData: IChartPoint[][],
        xScale: d3.ScaleTime<number, number>,
        yScale: d3.ScaleLinear<number, number>,
    ): React.ReactElement => {
        if (_.isEmpty(barData)) {
            barData = [[]];
        }
        const stackData = _.unzip(barData);
        return (
            <NodeGroup
                data={stackData}
                keyAccessor={this.keyAccessor}
                interpolation={this.interpolation}
                start={this.startBarTransition}
                enter={this.enterBarTransition}
                update={this.updateBarTransition}
                leave={this.leaveBarTransition}
                children={this.renderBarNodes}
            />
        );
    };

    private readonly renderLine = (
        lineData: IChartPoint[],
        xScale: d3.ScaleTime<number, number>,
        yScale: d3.ScaleLinear<number, number>,
    ): React.ReactElement => {
        if (_.isUndefined(lineData)) {
            lineData = [];
        }
        return (
            <NodeGroup
                data={[lineData]}
                keyAccessor={this.keyAccessor}
                interpolation={this.interpolation}
                start={this.startLineTransition}
                enter={this.enterLineTransition}
                update={this.updateLineTransition}
                leave={this.leaveLineTransition}
                children={this.renderLineNodes}
            />
        );
    };

    private readonly keyAccessor = (points: IChartPoint[], i: number): string => {
        const { granularity } = this.props;
        return `${granularity}-${i}`;
    };

    private interpolation(
        start: number | number[],
        end: number | number[],
        attr: string | undefined,
    ): ((t: number) => number) | ((t: number) => number[]) {
        if (attr === 'x' || attr === 'y') {
            return d3.interpolate<number[]>(start as number[], end as number[]);
        }
        return d3.interpolate(start as number, end as number);
    }

    private readonly startBarTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale] = createScales(timeline, granularity, chartRect);
        const interval = getInterval(granularity);
        const numDates = interval.count(timeline.startDate, timeline.endDate) + 1;
        const width = Math.max(1, _.floor(chartRect.width / (2 * numDates - 1)));
        const segments = points.length;
        // NB: output values are non-array, since no transitions can be applied
        return {
            segments,
            opacity: 1e-6,
            height: _.times(segments, (i: number): number => 0),
            width: _.times(segments, (i: number): number => width),
            x: _.times(segments, (i: number): number => xScale(points[0].date)),
            y: _.times(segments, (i: number): number => 0),
        };
    };
    private readonly enterBarTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const yScale = createScales(timeline, granularity, chartRect)[1];
        const offsets: number[] = points.map(
            (p: IChartPoint, i: number) => chartRect.height - yScale(_.sum(_.map(_.take(points, i), 'amount'))),
        );
        const heights = points.map((p: IChartPoint): number => {
            return chartRect.height - yScale(p.amount);
        });
        return {
            opacity: [0.7],
            height: [heights],
            y: [points.map((p: IChartPoint, i: number): number => offsets[i])],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly updateBarTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        const segments = points.length;
        const offsets: number[] = points.map(
            (p: IChartPoint, i: number) => chartRect.height - yScale(_.sum(_.map(_.take(points, i), 'amount'))),
        );
        const heights = points.map((p: IChartPoint): number => {
            return chartRect.height - yScale(p.amount);
        });
        const interval = getInterval(granularity);
        const numDates = interval.count(timeline.startDate, timeline.endDate) + 1;
        const width = Math.max(1, _.floor(chartRect.width / (2 * numDates - 1)));
        return {
            opacity: [0.8],
            height: [heights],
            width: [_.times(segments, () => width)],
            x: [_.times(segments, (i: number): number => xScale(points[0].date))],
            y: [points.map((p: IChartPoint, i: number): number => offsets[i])],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly leaveBarTransition = (points: IChartPoint[]): object => {
        return {
            height: 0,
            opacity: 0.3,
            timing: {
                duration: 400,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly renderBarNodes = (nodes: INode[]): React.ReactElement => {
        const { chartRect } = this.props;
        const stacks = nodes.map(({ key, state }) => {
            return _.times(state.segments, (i: number) => {
                // setup stack height and save for next segment
                const height = _.get(state.height, i, state.height) as number;
                const width = _.get(state.width, i, state.width) as number;
                const x = _.get(state.x, i, state.x) as number;
                const y = _.get(state.y, i, state.y) as number;

                const k = `${key}-${i}`;
                return (
                    <S.BarRect
                        ord={(i as number) + 1}
                        height={height}
                        width={width}
                        x={x - width / 2}
                        y={chartRect.height - height - y}
                        key={k}
                    />
                );
            });
        });
        return <>{stacks}</>;
    };

    private readonly startLineTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 1e-6,
            x: points.map((p: IChartPoint): number => xScale(p.date)),
            y: points.map((p: IChartPoint): number => chartRect.bottom),
        };
    };
    private readonly enterLineTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 1,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => yScale(p.amount))],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly updateLineTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale, yScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 0.8,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => yScale(p.amount))],
            timing: {
                duration: 800,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly leaveLineTransition = (points: IChartPoint[]): object => {
        const { chartRect, granularity, timeline } = this.props;
        const [xScale] = createScales(timeline, granularity, chartRect);
        return {
            opacity: 0.3,
            x: [points.map((p: IChartPoint): number => xScale(p.date))],
            y: [points.map((p: IChartPoint): number => chartRect.bottom)],
            timing: {
                duration: 400,
                ease: d3.easeExpInOut,
            },
        };
    };
    private readonly renderLineNodes = (nodes: INode[]): React.ReactElement => {
        const lineGroups = nodes.map(({ key, state }, i) => {
            const path = d3
                .line<number[]>()
                .x((arr: number[]): number => arr[0])
                .y((arr: number[]): number => arr[1]);
            const line: number[][] = _.zip(state.x as number[], state.y as number[]) as number[][];
            const dots: React.ReactElement[] = line.map(
                (p: number[], j: number): React.ReactElement => {
                    return <S.LineDot key={`${key}-${i}-${j}`} ord={i} r={3} cx={p[0]} cy={p[1]} />;
                },
            );
            return (
                <S.Line key={`${key}-${i}`} opacity={state.opacity}>
                    <S.LinePath d={path(line) as string} ord={i} />
                    {dots}
                </S.Line>
            );
        });
        return <>{lineGroups}</>;
    };
}
